import { NavigationService } from "@/shared/services/NavigationService";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { Component, Inject, Prop, Vue, Watch } from "vue-property-decorator";
import {
  CHAT_ACTIONS,
  CHAT_ACTIONS_RESPONSES,
  CHAT_BOT_ROLES,
  CHAT_BOT_TEXT_UTILS,
  CHAT_ERRORS,
  CHAT_IFRAME_CONFIG,
  CHAT_MULTIMEDIA_CONFIG,
  CHAT_MULTIMEDIA_ERRORS
} from "./Constants/ChatWindow.constants";
import { buildCanvasFrame } from "./utils/canvasFrameBuilder";
import { mapGetters } from "vuex";
import store from "@/store";
import { STORE_CONSTANTS } from "@/shared/constants/GeneralConstants";
import { CHAT_BOT_CONSTANTS } from "@/shared/components/ChatBot/constants/ChatBotConstants";
import { ChatService } from "./Services/ChatWindow.service";
import { Http } from "@status/codes";
import Snackbar from "@/shared/components/Snackbar/Snackbar";
import { ChatComunication } from "./Model/ChatComunication.model";
import { ChatMessages } from "./Model/ChatMessages.model";
import { ChatAttachment } from "./Model/ChatAttachment.model";
import { ChatContext } from "@/shared/models/AIViewContext";
import { QuickAction } from "./Model/ChatResponse.model";
import { UserService } from "@/modules/User/services/UserService";
import { chatLinkHandlers } from "./utils/chatLinkHandlers";
import XLSX from "xlsx";

@Component({
  name: "ChatWindow",
  computed: {
    ...mapGetters({
      chatAction: "getChatComunication"
    })
  }
})
export default class ChatWindow extends Vue {
  @Prop({ default: false }) readonly isEmbedded!: boolean;
  @Prop() readonly patientId?: string;

  @Inject() chatService!: ChatService;
  @Inject() userService!: UserService;
  @Inject() navigationService!: NavigationService;

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  chatAction!: ChatComunication;
  SpeechRecognition =
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition;
  recognition: SpeechRecognition = this.SpeechRecognition
    ? new this.SpeechRecognition()
    : false;
  runtimeTranscription = "";
  error = "";

  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  CHAT_BOT_TEXTS = CHAT_BOT_TEXT_UTILS;
  CHAT_BOT_ROLES = CHAT_BOT_ROLES;
  ACTIONS = CHAT_ACTIONS;

  inputUser = "";
  messages: ChatMessages[] = [];
  quickActions: QuickAction[] = [];
  threadId = "";
  assistantId = "";
  showWindow = false;
  disableInput = false;
  socketResponse = false;
  loading = false;
  isExpanded = false;
  isFullscreen = false;
  isResizing = false;
  creatingSession = false;

  toggleFullscreenChat() {
    this.isFullscreen = !this.isFullscreen;
    if (this.isFullscreen) {
      this.isExpanded = false;
    }
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }
  resizeStartY = 0;
  resizeStartHeight = 460;
  currentHeight = 460;
  chatCardStyle: Record<string, string> = { height: "460px" };
  chatHistory: string[] = [];
  sessionId = "";
  historyRequestId = 0;
  summaryHistory = "";
  messagesContainer!: HTMLElement;
  history: {
    user: string;
    ai: string;
    dateCreated: number;
    attachments?: ChatAttachment[];
  }[] = [];
  context?: ChatContext;
  pendingAttachments: ChatAttachment[] = [];
  previewModalOpen = false;
  previewModalItem: ChatAttachment | null = null;
  canvasModalOpen = false;
  canvasModalHtml = "";
  canvasCache = new Map<
    string,
    { iframeId: string; dataUri: string; escapedCode: string }
  >();

  get baseConversationKey(): string {
    return this.patientId || this.$route.params.id || "general";
  }

  get conversationKey(): string {
    const dashboardId = this.activeDashboardId;
    return dashboardId
      ? `${this.baseConversationKey}:dashboard:${dashboardId}`
      : this.baseConversationKey;
  }

  get dashboardContextState(): any {
    return this.$store.getters["chatassistant/conversation"](
      this.baseConversationKey
    );
  }

  get chatAssistantState(): any {
    return this.$store.getters["chatassistant/conversation"](
      this.conversationKey
    );
  }

  get activeDashboardId(): string | undefined {
    if (this.dashboardContextState.selectedDashboardId) {
      return this.dashboardContextState.selectedDashboardId;
    }
    const data = this.dashboardContextState.activeViewContext?.data as
      | { dashboardId?: unknown }
      | undefined;
    return typeof data?.dashboardId === "string" ? data.dashboardId : undefined;
  }

  @Watch("activeDashboardId")
  async reloadDashboardHistory(
    dashboardId: string | undefined,
    previousDashboardId: string | undefined
  ) {
    if (dashboardId === previousDashboardId) return;
    if (!dashboardId) {
      this.historyRequestId += 1;
      this.syncConversationState();
      return;
    }
    void this.$store.dispatch("chatassistant/markAsRead", this.conversationKey);
    await this.getChatHistory();
  }

  @Watch("chatAssistantState", { deep: true })
  @Watch("dashboardContextState", { deep: true })
  syncConversationState() {
    const conversation = this.chatAssistantState;
    const dashboardState = this.dashboardContextState;
    this.messages = conversation.messages;
    this.history = conversation.history;
    this.sessionId = conversation.sessionId;
    this.summaryHistory = conversation.summaryHistory;
    this.quickActions = conversation.quickActions;
    this.loading = conversation.loading;
    this.disableInput =
      conversation.loading || dashboardState.activeViewLoading;
    const context: ChatContext = { ...(this.context || {}) };
    if (dashboardState.activeViewContext) {
      context.activeViewContext = dashboardState.activeViewContext;
    } else {
      delete context.activeViewContext;
    }
    this.context = Object.keys(context).length > 0 ? context : undefined;
  }

  @Watch("quickActions", { deep: true })
  scrollAfterQuickActionsChange() {
    this.scrollToBottom();
  }

  openCanvasModal(htmlCode: string) {
    const { dataUri } = buildCanvasFrame(htmlCode);
    this.canvasModalHtml = dataUri;
    this.canvasModalOpen = true;
  }

  closeCanvasModal() {
    this.canvasModalOpen = false;
    this.canvasModalHtml = "";
  }

  async createNewSession() {
    if (this.isEmbedded || this.creatingSession) return;

    this.creatingSession = true;
    try {
      const patientId = this.patientId || this.$route.params.id;
      const response = await this.chatService.createChatSession(patientId);
      const sessionId = response?.data?.sessionId;

      if (!sessionId) {
        throw new Error("La respuesta no contiene el identificador de sesión");
      }

      this.historyRequestId += 1;
      this.messages = [];
      this.history = [];
      this.summaryHistory = "";
      this.quickActions = [];
      this.sessionId = sessionId;
      this.$store.commit("chatassistant/setConversation", {
        key: this.conversationKey,
        conversation: {
          messages: [],
          history: [],
          summaryHistory: "",
          quickActions: [],
          sessionId,
          historyDashboardId: this.activeDashboardId
        }
      });
      this.syncConversationState();
      this.$nextTick(() => this.scrollToBottom());
    } catch (error) {
      console.error("Error creating chat session", error);
      Snackbar.popError("No fue posible crear una nueva sesión");
    } finally {
      this.creatingSession = false;
    }
  }

  handleIframeMessage = (event: MessageEvent) => {
    if (!event.data) return;
    if (event.data.type === "resize-iframe") {
      const { iframeId, height } = event.data;
      if (iframeId) {
        const iframeEl = document.getElementById(iframeId) as HTMLIFrameElement;
        if (iframeEl) {
          const clampedHeight = Math.min(
            Math.max(height, CHAT_IFRAME_CONFIG.MIN_HEIGHT),
            CHAT_IFRAME_CONFIG.MAX_HEIGHT
          );
          iframeEl.style.height = `${clampedHeight}px`;
        }
      }
    } else if (event.data.type === "canvas-link-click") {
      const { href, text } = event.data;
      const trimmedText = (text || "").trim();
      let strategy = chatLinkHandlers[trimmedText];
      if (!strategy) {
        const lowerText = trimmedText.toLowerCase();
        const foundKey = Object.keys(chatLinkHandlers).find(
          k => k.toLowerCase() === lowerText
        );
        if (foundKey) {
          strategy = chatLinkHandlers[foundKey];
        }
      }

      if (strategy) {
        if (this.canvasModalOpen) {
          this.closeCanvasModal();
        }
        strategy(href);
      } else {
        if (href) {
          if (href.startsWith("http://") || href.startsWith("https://")) {
            window.open(href, "_blank", "noopener,noreferrer");
          } else if (href.startsWith("/")) {
            if (this.canvasModalOpen) {
              this.closeCanvasModal();
            }
            NavigationService.navigateByPath(href);
          }
        }
      }
    }
  };

  beforeDestroy() {
    window.removeEventListener("message", this.handleIframeMessage);
    this.cleanPendingAttachments();
  }

  cleanPendingAttachments() {
    this.pendingAttachments.forEach(att => {
      if (att.previewUrl && att.previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(att.previewUrl);
      }
    });
    this.pendingAttachments = [];
  }

  async mounted() {
    window.addEventListener("message", this.handleIframeMessage);
    console.log("mounted chatwindow");
    this.inputUser = "";
    this.syncConversationState();

    this.showWindow = this.isEmbedded || (this.chatAction.chatOpen ?? false);

    if (this.showWindow) {
      await this.getContext();
    }

    this.threadId = this.chatAction.threadId ?? "";

    this.assistantId = this.chatAction.assistantId ?? "";

    this.scrollToBottom();

    this.$router.beforeEach((to, from, next) => {
      console.log("entro al router");
      this.messages = [];
      this.history = [];
      this.summaryHistory = "";
      this.sessionId = "";
      this.threadId = "";
      this.assistantId = "";
      /*   if (to.fullPath != LOGIN_PATH && from.fullPath != LOGIN_PATH) {
          const action: ChatComunication = {
            action: "BackUp",
            chatOpen: this.showWindow,
            msgsStore: this.messages,
            threadId: this.threadId,
            assistantId: this.assistantId
          };
          store.commit(STORE_CONSTANTS.CHAT_ACTION, action);
        }
        if (from.fullPath == LOGIN_PATH) {
          this.showWindow = false;
          this.messages = [];
          this.threadId = "";
          this.assistantId = "";
        } */
      next();
    });
  }

  scrollToBottom() {
    this.$nextTick(() => {
      const container = this.$refs.messagesContainer as HTMLElement | undefined;
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 0);
      }
    });
  }

  checkCompatibility() {
    if (!this.recognition) {
      this.error = CHAT_BOT_CONSTANTS.COMPATIBILITY_ERROR;
    }
  }

  handleMessageClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;

    const expandBtn = target?.closest(
      ".canvas-expand-btn"
    ) as HTMLElement | null;
    if (expandBtn) {
      event.preventDefault();
      event.stopPropagation();
      const code = expandBtn.getAttribute("data-code") || "";
      if (code) {
        this.openCanvasModal(code);
      }
      return;
    }

    const imgTarget = target?.closest("img") as HTMLImageElement | null;
    if (imgTarget) {
      event.preventDefault();
      const src =
        imgTarget.getAttribute("src") ||
        imgTarget.getAttribute("data-src") ||
        "";
      const alt = imgTarget.getAttribute("alt") || "Imagen";
      if (src) {
        this.openAttachmentPreview({
          name: alt,
          mimeType: "image/png",
          url: src
        });
      }
      return;
    }

    const link = target?.closest("a") as HTMLAnchorElement | null;
    if (!link) {
      return;
    }

    event.preventDefault();
    const href = link.getAttribute("href") || "";
    const text = link.innerText.trim();
    let strategy = chatLinkHandlers[text];

    if (!strategy) {
      const foundKey = Object.keys(chatLinkHandlers).find(
        key => key.toLowerCase() === text.toLowerCase()
      );
      if (foundKey) {
        strategy = chatLinkHandlers[foundKey];
      }
    }

    if (strategy) {
      strategy(href);
    } else if (href.startsWith("http://") || href.startsWith("https://")) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else if (href.startsWith("/")) {
      NavigationService.navigateByPath(href);
    }
  }

  async handleInputUser(functionCall: string) {
    const hasAttachments = this.pendingAttachments.length > 0;
    const hasText = this.inputUser.trim().length > 0;
    if (!hasText && !hasAttachments) {
      return;
    }

    if (this.dashboardContextState.activeViewLoading) {
      Snackbar.popInfo("Los datos del dashboard aún se están cargando.");
      return;
    }

    const attachmentsToSend = [...this.pendingAttachments];
    this.pendingAttachments = [];

    const userText = this.inputUser;
    this.inputUser = "";
    this.quickActions = [];

    const requestConversationKey = this.conversationKey;
    const requestConversation = this.chatAssistantState;
    const hadSession = Boolean(requestConversation.sessionId);
    const requestHistory = [...this.history];
    const requestSessionId = requestConversation.sessionId;
    const requestSummaryHistory = this.summaryHistory;
    const currentContext: ChatContext = { ...(this.context || {}) };
    if (this.dashboardContextState.activeViewContext) {
      currentContext.activeViewContext = this.dashboardContextState.activeViewContext;
    } else {
      delete currentContext.activeViewContext;
    }

    await this.getUserBasicInfo(this.patientId || this.$route.params.id);
    console.log("this.inputUser--->", userText);
    this.disableInput = true;
    const response = await this.$store.dispatch("chatassistant/generate", {
      key: requestConversationKey,
      input: {
        patientId: this.patientId || this.$route.params.id,
        viewName: this.$route.name as string,
        message: userText,
        history: requestHistory,
        sessionId: requestSessionId,
        summaryHistory: requestSummaryHistory,
        context:
          Object.keys(currentContext).length > 0 ? currentContext : undefined,
        attachments: attachmentsToSend
      },
      functionCall: (functionCall || "sendChatProp") as
        | "sendChatProp"
        | "getServiceSummary"
        | "getDocumentSummary",
      userText,
      attachments: attachmentsToSend
    });
    this.disableInput = false;
    console.log("response--->", response);
    if (response.code == Http.Ok) {
      console.log("response.data?.sessionId--->", response.data);
      if (response.data?.sessionId) {
        if (!hadSession) {
          this.$emit("session-created", this.patientId);
        }
      }
      const navigationCommand =
        response.data?.finalResponseToUser ||
        (response as any).finalResponseToUser;
      if (navigationCommand?.targetView) {
        const targetView = navigationCommand.targetView;
        store.commit(STORE_CONSTANTS.CHAT_ACTION, {
          action: "",
          chatOpen: true,
          msgsStore: []
        });
        if (navigationCommand.paramsView) {
          NavigationService.navigateTo(
            targetView,
            navigationCommand.paramsView
          );
        } else {
          NavigationService.navigateTo(targetView);
        }
      }
    } else {
      this.disableInput = false;
      Snackbar.popError(CHAT_ERRORS.ERROR_RESPONSE);
    }
  }

  handleQuickAction(action: QuickAction) {
    if (this.disableInput || this.loading || !action.value) {
      return;
    }

    this.inputUser = action.value;
    this.handleInputUser("sendChatProp");
  }
  closeChatWindow() {
    console.log("closeChatWindow");
    this.showWindow = false;
    this.isExpanded = false;
    this.isFullscreen = false;
    this.endSpeechRecognition();
    const action: ChatComunication = {
      action: this.ACTIONS.CLOSE_CHAT
    };

    store.commit(STORE_CONSTANTS.CHAT_ACTION, action);
  }

  toggleExpandChat() {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      this.isFullscreen = false;
    }
    this.currentHeight = this.isExpanded
      ? Math.min(this.currentHeight, window.innerHeight - 40)
      : 460;
    this.chatCardStyle = { height: `${this.currentHeight}px` };
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  @Watch("chatAction")
  async handleChatAction() {
    const action = this.chatAction.action;
    if (action == "" || action == this.ACTIONS.CLOSE_CHAT) {
      return;
    }

    switch (action) {
      case this.ACTIONS.OPEN_CHAT: {
        await this.getContext();
        this.showWindow = true;
        await this.$nextTick();
        this.scrollToBottom();
        break;
      }
      case this.ACTIONS.START_SPEECH_RECORD: {
        //this.startSpeechRecognition();
        break;
      }
      case this.ACTIONS.STOP_SPEECH_RECORD: {
        this.endSpeechRecognition();
        break;
      }
    }
    this.disengageAction();
  }

  disengageAction() {
    store.commit(STORE_CONSTANTS.CHAT_ACTION, {
      action: ""
    });
  }

  async getContext() {
    try {
      const patientId = this.patientId || this.$route.params.id;
      await Promise.all([
        this.getChatHistory(),
        patientId ? this.getUserBasicInfo(patientId) : Promise.resolve()
      ]);
      if (this.chatAction.msgsStore?.length) {
        this.inputUser = this.chatAction.msgsStore[0].content as string;
        const storedContext = this.chatAction.msgsStore[0].context;
        if (storedContext) {
          this.context = {
            ...(this.context || {}),
            ...storedContext
          };
        }
        this.handleInputUser(
          this.chatAction.msgsStore[0].functionCall?.name as string
        );
      }
    } catch (error) {
      console.error("Error fetching context:", error);
    }
  }

  async getUserBasicInfo(patientId?: string) {
    try {
      if (!patientId) return;
      const response = await this.userService.getUserBasicInfoIA(patientId);
      if (response.code == Http.Found) {
        console.log("response user", response.data);
        this.context = {
          ...(this.context || {}),
          userData: response.data
        };
      }
    } catch (error) {
      console.error("Error fetching user basic info:", error);
    }
  }

  /*   startSpeechRecognition() {
    if (!this.recognition) {
      return false;
    }
    this.recognition.lang = CHAT_BOT_CONSTANTS.RECOGNITION_LANG;
    this.recognition.interimResults = true;

    this.recognition.addEventListener("result", event => {
      const text = Array.from(event.results)
        .map(result => result[0])
        .map(result => result.transcript)
        .join("");
      this.runtimeTranscription = text;
    });

    this.recognition.addEventListener("end", () => {
      if (this.runtimeTranscription !== "") {
        this.setText(this.capitalizeFirstLetter(this.runtimeTranscription));
      }

      this.runtimeTranscription = "";
      this.recognition.stop();
    });

    this.recognition.start();
  } */

  endSpeechRecognition() {
    this.recognition.stop();
  }

  capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  setText(string: string) {
    this.inputUser = string;
  }

  formatMessageContent(content: string): string {
    if (!content) return "";

    const isRawHtml = /^\s*<[a-z][\s\S]*>/i.test(content.trim());
    if (isRawHtml) {
      const markdownImageRegex = /!\[([\s\S]*?)\]\(([^)]+)\)/g;
      return content.replace(markdownImageRegex, (match, altText, imageUrl) => {
        const cleanUrl = imageUrl
          .replace(/&amp;/g, "&")
          .replace(/\s+/g, "")
          .trim();
        const cleanAlt = altText.replace(/\s+/g, " ").trim();
        return `<img src="${cleanUrl}" alt="${cleanAlt}" class="chat-attachment-img chat-inline-generated-img" title="Haz clic para ampliar: ${cleanAlt}" data-src="${cleanUrl}" />`;
      });
    }

    const imageBlocks: string[] = [];
    let html = content.replace(
      /!\[([\s\S]*?)\]\(([^)]+)\)/g,
      (match, altText, imageUrl) => {
        const cleanUrl = imageUrl
          .replace(/&amp;/g, "&")
          .replace(/\s+/g, "")
          .trim();
        const cleanAlt = altText.replace(/\s+/g, " ").trim();
        console.log("Generated image URL:", cleanUrl);
        const index = imageBlocks.length;
        imageBlocks.push(
          `<img src="${cleanUrl}" alt="${cleanAlt}" class="chat-attachment-img chat-inline-generated-img" title="Haz clic para ampliar: ${cleanAlt}" data-src="${cleanUrl}" />`
        );
        return `%%IMGBLOCK${index}%%`;
      }
    );

    const canvasBlocks: string[] = [];
    html = html.replace(
      /```\s*html[\r\n]*([\s\S]*?)(```|$)/gi,
      (match, code, closing) => {
        const cleanCode = code.trim();
        const isComplete = closing === "```";

        if (!isComplete && cleanCode.length < 10) {
          const index = canvasBlocks.length;
          canvasBlocks.push(
            `<div class="chat-canvas-loading" style="margin: 10px 0; padding: 10px 14px; border: 1px dashed var(--v-accent-base, #63c7e7); border-radius: 8px; background: #f0f9fc; color: #0288d1; font-size: 13px; font-weight: 500; display: flex; align-items: center; gap: 8px;">` +
              `<i class="v-icon notranslate mdi mdi-loading mdi-spin theme--light" style="font-size: 18px;"></i>` +
              `<span>Generando Canvas interactivo...</span>` +
              `</div>`
          );
          return `%%CANVASBLOCK${index}%%`;
        }

        let cached = this.canvasCache.get(cleanCode);
        if (!cached) {
          cached = buildCanvasFrame(cleanCode);
          this.canvasCache.set(cleanCode, cached);
        }
        const { iframeId, dataUri, escapedCode } = cached;

        const index = canvasBlocks.length;
        canvasBlocks.push(
          `<div class="chat-canvas-card" style="margin: 10px 0; border: 1px solid var(--v-accent-base, #63c7e7); border-radius: 8px; overflow: hidden; background: #ffffff; box-shadow: 0 2px 8px rgba(0,0,0,0.08); width: 100%; box-sizing: border-box;">` +
            `<div class="chat-canvas-header" style="background: var(--v-accent-base, #63c7e7); color: #ffffff; padding: 6px 12px; font-weight: 600; font-size: 12px; display: flex; justify-content: space-between; align-items: center;">` +
            `<span style="display: flex; align-items: center; gap: 6px;">` +
            `<i class="v-icon notranslate mdi mdi-palette-outline" style="font-size: 16px; color: #fff;"></i> Vista Previa` +
            `</span>` +
            `<button type="button" class="canvas-expand-btn" data-code="${escapedCode}" style="background: rgba(255,255,255,0.25); border: none; color: #ffffff; padding: 4px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">` +
            `<i class="v-icon notranslate mdi mdi-fullscreen" style="font-size: 14px; color: #fff;"></i> Abrir en Modal` +
            `</button>` +
            `</div>` +
            `<iframe ` +
            `id="${iframeId}" ` +
            `src="${dataUri}" ` +
            `sandbox="allow-scripts allow-popups allow-forms allow-modals" ` +
            `style="width: 100%; height: 180px; border: none; display: block; background: #ffffff; transition: height 0.2s ease;" ` +
            `loading="lazy"` +
            `></iframe>` +
            `</div>`
        );
        return `%%CANVASBLOCK${index}%%`;
      }
    );

    // Bloques de código multilínea (```código```)
    html = html.replace(
      /```([\s\S]*?)```/g,
      '<pre style="background: #f4f4f4; padding: 8px; border-radius: 4px; overflow-x: auto; margin: 4px 0;"><code>$1</code></pre>'
    );

    // Código inline (`código`)
    html = html.replace(
      /`([^`]+)`/g,
      '<code style="background: #e9ecef; padding: 2px 4px; border-radius: 3px; font-family: monospace;">$1</code>'
    );

    // Encabezados (# H1, ## H2, ### H3, #### H4)
    html = html.replace(
      /^#### (.*$)/gim,
      '<h4 style="font-weight: bold; margin: 4px 0;">$1</h4>'
    );
    html = html.replace(
      /^### (.*$)/gim,
      '<h3 style="font-weight: bold; margin: 6px 0;">$1</h3>'
    );
    html = html.replace(
      /^## (.*$)/gim,
      '<h2 style="font-weight: bold; margin: 8px 0;">$1</h2>'
    );
    html = html.replace(
      /^# (.*$)/gim,
      '<h1 style="font-weight: bold; margin: 10px 0;">$1</h1>'
    );

    // Negrita (**texto** o __texto__)
    html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/__(.*?)__/g, "<strong>$1</strong>");

    // Cursiva (*texto* o _texto_)
    html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
    html = html.replace(/_(.*?)_/g, "<em>$1</em>");

    // Enlaces ([texto](url))
    html = html.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1976d2; text-decoration: underline;">$1</a>'
    );

    // Viñetas / Listas desordenadas (- elemento o * elemento)
    html = html.replace(/^\s*[-*]\s+(.*$)/gim, "• $1");

    // Saltos de línea
    html = html.replace(/\n/g, "<br />");

    // Re-insert clean Canvas HTML blocks
    canvasBlocks.forEach((block, index) => {
      html = html.replace(`%%CANVASBLOCK${index}%%`, block);
    });

    // Re-insert clean Image blocks
    imageBlocks.forEach((block, index) => {
      html = html.replace(`%%IMGBLOCK${index}%%`, block);
    });

    return html;
  }

  handleExtraParams(action: string, value: string) {
    if (action == "" || value == "") {
      Snackbar.popError(CHAT_ERRORS.ACTION_ERROR);
      return;
    }

    switch (action) {
      case CHAT_ACTIONS_RESPONSES.NAVEGATING_TO:
        NavigationService.navigateByPath(value);
        break;
      default:
        Snackbar.popError(CHAT_ERRORS.ACTION_ERROR);
        break;
    }
  }

  getAttachmentName(att: ChatAttachment | any): string {
    if (!att) return "Archivo adjunto";
    if (typeof att === "string") {
      const parts = att.split("/");
      return parts[parts.length - 1] || att;
    }
    return (
      att.name ||
      att.fileName ||
      att.filename ||
      att.file_name ||
      att.originalName ||
      att.originalname ||
      att.original_name ||
      att.tempName ||
      att.tempname ||
      att.temp_name ||
      att.tempFileName ||
      att.temp_file_name ||
      att.title ||
      att.label ||
      att.file?.name ||
      att.file?.fileName ||
      "Archivo adjunto"
    );
  }

  inferMimeTypeFromName(name: string): string {
    const n = (name || "").toLowerCase();
    if (n.endsWith(".pdf")) return "application/pdf";
    if (n.endsWith(".xlsx") || n.endsWith(".xls"))
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (n.endsWith(".docx") || n.endsWith(".doc")) return "application/msword";
    if (n.endsWith(".txt")) return "text/plain";
    if (n.endsWith(".csv")) return "text/csv";
    if (/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(n)) return "image/png";
    if (/\.(mp4|webm|avi|mov)$/i.test(n)) return "video/mp4";
    return "application/octet-stream";
  }

  async getChatHistory() {
    try {
      const patientId = this.patientId || this.$route.params.id;
      const dashboardId = this.activeDashboardId;
      const conversationKey = this.conversationKey;
      const cachedConversation = this.chatAssistantState;
      if (
        cachedConversation.historyDashboardId === this.activeDashboardId &&
        (cachedConversation.loading || cachedConversation.messages.length > 0)
      ) {
        this.syncConversationState();
        return;
      }

      const requestId = ++this.historyRequestId;
      this.messages = [];
      this.history = [];
      this.summaryHistory = "";
      this.quickActions = [];
      this.sessionId = "";
      this.$store.commit("chatassistant/setConversation", {
        key: conversationKey,
        conversation: {
          messages: [],
          history: [],
          summaryHistory: "",
          quickActions: [],
          sessionId: "",
          historyDashboardId: dashboardId
        }
      });

      if (dashboardId) {
        await this.chatService.ensureDashboard(patientId, dashboardId);
        if (requestId !== this.historyRequestId) return;
      }

      const response = await this.chatService.getChatHistory(
        patientId,
        dashboardId
      );
      if (requestId !== this.historyRequestId) return;
      console.log("getChatHistory response", response);
      const historyData = response.data || (response as any);
      this.summaryHistory = historyData?.summaryHistory ?? "";
      this.quickActions = (historyData?.quickActions || []).filter(
        (action: { label: string; value: string }) =>
          action && action.label && action.value
      );
      if (response.code == Http.Ok) {
        const history = response.data?.history ?? [];
        this.history = history;
        this.quickActions = (response.data?.quickActions || []).filter(
          action => action && action.label && action.value
        );

        history.forEach(message => {
          let userText = message.user || "";
          let attachments: ChatAttachment[] = message.attachments || [];

          if (attachments.length === 0 && userText) {
            const match = userText.match(/\[Archivos adjuntos:\s*([^\]]+)\]/i);
            if (match && match[1]) {
              const fileNames = match[1].split(",").map(s => s.trim());
              attachments = fileNames.map(name => ({
                name: name,
                mimeType: this.inferMimeTypeFromName(name)
              }));
            }
          }

          if (attachments.length > 0) {
            userText = userText
              .replace(/\[Archivos adjuntos:\s*[^\]]+\]/gi, "")
              .trim();
          }

          const user: ChatMessages = {
            role: "user",
            content: userText,
            attachments: attachments
          };

          this.messages.push(user);

          const ia: ChatMessages = {
            role: "assistant",
            content: message.ai
          };

          this.messages.push(ia);
        });
        this.sessionId = response.data?.sessionId ?? "";
        this.$store.commit("chatassistant/setConversation", {
          key: conversationKey,
          conversation: {
            messages: this.messages,
            history: this.history,
            sessionId: this.sessionId,
            historyDashboardId: dashboardId,
            quickActions: this.quickActions,
            summaryHistory: this.summaryHistory
          }
        });
        this.syncConversationState();
        this.scrollToBottom();
      } else if (response.code === Http.NotFound) {
        // No active session is expected for a new chat.
        this.history = [];
        this.sessionId = "";
        this.$store.commit("chatassistant/setConversation", {
          key: conversationKey,
          conversation: {
            messages: [],
            history: [],
            quickActions: this.quickActions,
            summaryHistory: this.summaryHistory,
            sessionId: "",
            historyDashboardId: dashboardId
          }
        });
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    }
  }

  triggerFileInput() {
    const input = this.$refs.fileInput as HTMLInputElement | undefined;
    if (input) {
      input.value = "";
      input.click();
    }
  }

  isDraggingOver = false;

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFilesSelected(files);
    }
  }

  async handleFilesSelected(event: Event | FileList | File[]) {
    let files: FileList | File[] | null = null;
    if (
      event &&
      typeof event === "object" &&
      "target" in event &&
      (event as Event).target
    ) {
      const target = (event as Event).target as HTMLInputElement;
      files = target.files;
    } else {
      files = event as FileList | File[];
    }
    if (!files || files.length === 0) return;

    if (
      this.pendingAttachments.length + files.length >
      CHAT_MULTIMEDIA_CONFIG.MAX_FILES
    ) {
      Snackbar.popError(CHAT_MULTIMEDIA_ERRORS.MAX_FILES_EXCEEDED);
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!this.isSupportedFile(file)) {
        Snackbar.popError(CHAT_MULTIMEDIA_ERRORS.INVALID_FILE_TYPE);
        continue;
      }

      if (file.size > CHAT_MULTIMEDIA_CONFIG.MAX_FILE_SIZE_BYTES) {
        Snackbar.popError(CHAT_MULTIMEDIA_ERRORS.FILE_TOO_LARGE);
        continue;
      }

      try {
        let pureBase64 = "";
        let mimeType = this.getMimeTypeFromFile(file);

        if (this.isExcelFile(file)) {
          pureBase64 = await this.parseExcelToCsvBase64(file);
          mimeType = "text/csv";
        } else {
          const base64Data = await this.fileToBase64(file);
          pureBase64 = base64Data.split(",")[1] || base64Data;
        }

        this.pendingAttachments.push({
          name: file.name,
          mimeType: mimeType,
          data: pureBase64,
          previewUrl: URL.createObjectURL(file),
          size: file.size
        });
      } catch (error) {
        console.error("Error reading file:", error);
      }
    }
    this.scrollToBottom();
  }

  isExcelFile(file: File): boolean {
    if (!file) return false;
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();
    return (
      type.includes("excel") ||
      type.includes("spreadsheet") ||
      name.endsWith(".xlsx") ||
      name.endsWith(".xls")
    );
  }

  async parseExcelToCsvBase64(file: File): Promise<string> {
    const arrayBuffer = await this.fileToArrayBuffer(file);
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    let csvContent = "";

    workbook.SheetNames.forEach(sheetName => {
      const worksheet = workbook.Sheets[sheetName];
      const csv = XLSX.utils.sheet_to_csv(worksheet);
      if (csv && csv.trim()) {
        if (workbook.SheetNames.length > 1) {
          csvContent += `--- Hoja: ${sheetName} ---\n`;
        }
        csvContent += csv + "\n\n";
      }
    });

    if (!csvContent.trim()) {
      csvContent = "(Archivo Excel sin contenido de datos)";
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(csvContent);
    let binary = "";
    for (let i = 0; i < data.length; i++) {
      binary += String.fromCharCode(data[i]);
    }
    return window.btoa(binary);
  }

  fileToArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = error => reject(error);
      reader.readAsArrayBuffer(file);
    });
  }

  getMimeTypeFromFile(file: File): string {
    if (file.type) return file.type;
    const name = file.name.toLowerCase();
    if (name.endsWith(".pdf")) return "application/pdf";
    if (name.endsWith(".txt")) return "text/plain";
    if (name.endsWith(".docx"))
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    if (name.endsWith(".doc")) return "application/msword";
    if (name.endsWith(".xlsx"))
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    if (name.endsWith(".xls")) return "application/vnd.ms-excel";
    if (name.endsWith(".csv")) return "text/csv";
    if (name.endsWith(".json")) return "application/json";
    if (name.endsWith(".xml")) return "application/xml";
    return "application/octet-stream";
  }

  isSupportedFile(file: File): boolean {
    const mimeType = this.getMimeTypeFromFile(file);
    if (
      mimeType.startsWith("image/") ||
      mimeType.startsWith("video/") ||
      mimeType.startsWith("text/")
    ) {
      return true;
    }
    const documentMimeTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-powerpoint",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/json",
      "application/xml",
      "application/rtf",
      "text/csv"
    ];
    if (documentMimeTypes.includes(mimeType)) {
      return true;
    }
    const documentExtensions = [
      ".pdf",
      ".txt",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".csv",
      ".json",
      ".xml",
      ".ppt",
      ".pptx",
      ".rtf"
    ];
    const nameLower = file.name.toLowerCase();
    return documentExtensions.some(ext => nameLower.endsWith(ext));
  }

  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  removePendingAttachment(index: number) {
    const removed = this.pendingAttachments.splice(index, 1);
    if (removed[0]?.previewUrl && removed[0].previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(removed[0].previewUrl);
    }
  }

  isImage(att: ChatAttachment | any): boolean {
    if (!att) return false;
    const mime = (att.mimeType || att.type || "").toLowerCase();
    const name = this.getAttachmentName(att).toLowerCase();
    return (
      mime.startsWith("image/") ||
      /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name)
    );
  }

  isVideo(att: ChatAttachment | any): boolean {
    if (!att) return false;
    const mime = (att.mimeType || att.type || "").toLowerCase();
    const name = this.getAttachmentName(att).toLowerCase();
    return (
      mime.startsWith("video/") || /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(name)
    );
  }

  isDocument(att: ChatAttachment | any): boolean {
    return Boolean(att && !this.isImage(att) && !this.isVideo(att));
  }

  isPdf(att: ChatAttachment | any): boolean {
    if (!att) return false;
    const mime = (att.mimeType || att.type || "").toLowerCase();
    const name = this.getAttachmentName(att).toLowerCase();
    return mime.includes("pdf") || name.endsWith(".pdf");
  }

  getFileIcon(att: ChatAttachment | any): string {
    if (!att) return "mdi-file-document-outline";
    const name = this.getAttachmentName(att).toLowerCase();
    const mime = (att.mimeType || att.type || "").toLowerCase();

    if (mime.includes("pdf") || name.endsWith(".pdf")) {
      return "mdi-file-pdf-box";
    }
    if (
      mime.includes("word") ||
      name.endsWith(".doc") ||
      name.endsWith(".docx")
    ) {
      return "mdi-file-word";
    }
    if (
      mime.includes("excel") ||
      mime.includes("spreadsheet") ||
      name.endsWith(".xls") ||
      name.endsWith(".xlsx") ||
      name.endsWith(".csv")
    ) {
      return "mdi-file-excel";
    }
    if (mime.startsWith("text/") || name.endsWith(".txt")) {
      return "mdi-file-document-outline";
    }
    if (
      mime.includes("presentation") ||
      mime.includes("powerpoint") ||
      name.endsWith(".ppt") ||
      name.endsWith(".pptx")
    ) {
      return "mdi-file-powerpoint";
    }
    if (mime.startsWith("video/")) {
      return "mdi-video";
    }
    if (mime.startsWith("image/")) {
      return "mdi-image";
    }
    return "mdi-file-document-outline";
  }

  getAttachmentSource(att: ChatAttachment | any): string {
    if (!att) return "";
    if (typeof att === "string") return att;
    if (att.previewUrl) {
      return att.previewUrl;
    }
    if (att.data) {
      return `data:${att.mimeType || "application/octet-stream"};base64,${
        att.data
      }`;
    }
    if (att.fileUri) {
      return att.fileUri;
    }
    if (att.url) {
      return att.url;
    }
    if (att.uri) {
      return att.uri;
    }
    if (att.path) {
      return att.path;
    }
    if (att.src) {
      return att.src;
    }
    return "";
  }

  downloadAttachment(att: ChatAttachment | any) {
    if (!att) return;
    const src = this.getAttachmentSource(att);
    if (!src) return;

    const link = document.createElement("a");
    link.href = src;
    link.download = this.getAttachmentName(att);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  openAttachmentPreview(attachment: ChatAttachment) {
    this.previewModalItem = attachment;
    this.previewModalOpen = true;
  }

  closeAttachmentPreview() {
    this.previewModalOpen = false;
    this.previewModalItem = null;
  }
}
